import React, { useEffect, useState } from 'react';
import { upscaleConfig, subscribeUpscaleConfig, UPSCALE_DEFAULTS } from '../utils/upscaleDefaults';
import { saveUpscaleConfig } from '../data/upscaleConfigSync';

const clone = (o) => JSON.parse(JSON.stringify(o));

// [key, label, min, max, step, hint]
const SUPIR_FIELDS = [
  ['steps', 'Steps (higher = slower, marginally cleaner)', 8, 60, 1],
  ['cfg', 'Prompt strength / cfg (low = honest, high = punchy/AI)', 1, 9, 0.5],
  ['restore', 'Fidelity to source (higher = truer, less invention)', -1, 8, 0.5],
  ['churn', 'Stochastic churn (0 = no fabricated micro-detail)', 0, 10, 0.5],
  ['snoise', 'Sampler noise', 0.9, 1.1, 0.001],
  ['refineDenoise', 'Two-stage finish denoise (two-stage only)', 0, 0.6, 0.01],
  ['seed', 'Seed', 0, 999999, 1],
];
const POLL_FIELDS = [
  ['stallMs', 'Stall timeout — fail after no progress for (min)', 1, 30, 1, 60000],
  ['capMs', 'Absolute cap — hard backstop (min)', 5, 180, 5, 60000],
];

export default function UpscaleConfigPanel({ onClose }) {
  const [draft, setDraft] = useState(() => clone(upscaleConfig()));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => subscribeUpscaleConfig((c) => setDraft((d) => ({ ...clone(c), ...d }))), []);

  const supir = draft.models?.supir || {};
  const poll = draft.poll || {};
  const setSupir = (key, v) => {
    setMsg('');
    setDraft((d) => ({ ...d, models: { ...d.models, supir: { ...d.models?.supir, [key]: v } } }));
  };
  const setPoll = (key, v) => {
    setMsg('');
    setDraft((d) => ({ ...d, poll: { ...d.poll, [key]: v } }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await saveUpscaleConfig({ poll: draft.poll, models: draft.models });
      setMsg('Saved — applies to the next upscale, no deploy.');
    } catch (e) { setMsg(`Save failed: ${e.message}`); }
    setBusy(false);
  };
  const reset = () => { setDraft(clone(UPSCALE_DEFAULTS)); setMsg('Reset to defaults (not yet saved).'); };

  return (
    <div className="cs-cfg-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cs-cfg-modal">
        <div className="cs-cfg-head">
          <div className="cs-cfg-title">Upscale settings (live)</div>
          <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="cs-cfg-sub">
          Stored in Firestore (<code>config/upscale</code>) — the GPU drainer reads them per job, so tuning applies to the <b>next upscale with no deploy</b>. Defaults are a gentle, honest de-blur (minimal AI invention).
        </div>
        <div className="cs-cfg-body">
          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">SUPIR restore → upscale</div>
            <div className="cs-cfg-note">
              The detail-recovery upscaler for soft/tight crops. <b>Mode</b> picks the pipeline: <b>SUPIR-only</b> is one pass (seam-free, ~half the time); <b>two-stage</b> adds an Ultimate-SD finish (more detail, can over-invent + seam). For an honest look keep cfg low, churn 0, fidelity high, and don’t ask the prompt for extra spray/texture.
            </div>
            <label className="cs-cfg-field" style={{ marginBottom: 10 }}>
              <span className="cs-cfg-label">Pipeline mode</span>
              <select value={supir.mode || 'supir-only'} onChange={(e) => setSupir('mode', e.target.value)}>
                <option value="supir-only">SUPIR-only (seam-free, faster)</option>
                <option value="two-stage">Two-stage (SUPIR → Ultimate SD)</option>
              </select>
            </label>
            <div className="cs-cfg-grid">
              {SUPIR_FIELDS.map(([key, label, min, max, step]) => (
                <label key={key} className="cs-cfg-field">
                  <span className="cs-cfg-label">{label}</span>
                  <input
                    type="number" min={min} max={max} step={step}
                    value={supir[key] ?? ''}
                    onChange={(e) => setSupir(key, e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </label>
              ))}
            </div>
            <label className="cs-cfg-field" style={{ marginTop: 10 }}>
              <span className="cs-cfg-label">Positive prompt (what it IS — keep neutral/honest)</span>
              <textarea rows={2} value={supir.positive ?? ''} onChange={(e) => setSupir('positive', e.target.value)} />
            </label>
            <label className="cs-cfg-field" style={{ marginTop: 8 }}>
              <span className="cs-cfg-label">Negative prompt (what to avoid — anti-painting/anti-fake)</span>
              <textarea rows={3} value={supir.negative ?? ''} onChange={(e) => setSupir('negative', e.target.value)} />
            </label>
          </div>
          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">Progress watchdog</div>
            <div className="cs-cfg-note">
              Completion is driven by real streamed progress; the job only fails on a genuine stall (no progress for the stall window) or the absolute cap. Never a naive fixed timeout.
            </div>
            <div className="cs-cfg-grid">
              {POLL_FIELDS.map(([key, label, min, max, step, factor]) => (
                <label key={key} className="cs-cfg-field">
                  <span className="cs-cfg-label">{label}</span>
                  <input
                    type="number" min={min} max={max} step={step}
                    value={Number.isFinite(poll[key]) ? Math.round(poll[key] / factor) : ''}
                    onChange={(e) => setPoll(key, e.target.value === '' ? '' : Number(e.target.value) * factor)}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="cs-cfg-foot">
          {msg && <span className="cs-cfg-msg">{msg}</span>}
          <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={reset} disabled={busy}>Reset to defaults</button>
          <button className="cs-btn cs-btn-sm cs-btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save (live)'}</button>
        </div>
      </div>
    </div>
  );
}
